import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { DateRangeProvider } from './contexts/DateRangeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PipedreamProvider } from './contexts/PipedreamContext';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { router } from './router';
import { queryClient } from './lib/react-query';
import { setupInstallPrompt, setupOfflineDetection } from './utils/pwa';
import { useEffect } from 'react';
import { PWAUpdatePrompt } from './components/System/PWAUpdatePrompt';

function App() {
  // Initialize PWA features.
  //
  // The service worker is auto-registered by vite-plugin-pwa (Workbox) — we do NOT
  // register one manually. The previous `registerServiceWorker()` call here pointed
  // at `/service-worker.js`, a file that doesn't exist in this repo; it was either a
  // no-op or fighting Workbox for control of the same scope. Removing it eliminates
  // a suspect in the intermittent login reload loop. Also unregisters any legacy SW
  // from earlier deploys on first boot so stale bundles cannot serve auth responses.
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    void (async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          const scriptUrl = reg.active?.scriptURL ?? reg.installing?.scriptURL ?? reg.waiting?.scriptURL ?? '';
          // Keep only the Workbox-generated SW; drop any legacy /service-worker.js
          // or unknown scripts that could be caching stale auth responses.
          if (scriptUrl && !/\/(sw|registerSW)\.(js|ts)$/.test(scriptUrl)) {
            await reg.unregister();
          }
        }
      } catch (err) {
        // Non-fatal: SW registration cleanup is best-effort.
        console.warn('[App] SW cleanup skipped:', err);
      }
    })();

    setupInstallPrompt();
    setupOfflineDetection();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <DateRangeProvider>
              <ToastProvider>
                <PWAUpdatePrompt />
                <PipedreamProvider>
                  {/* NOTE: CrmCommandPalette uses useNavigate() and MUST
                      be mounted inside the Router tree. It was originally
                      placed here as a sibling of RouterProvider, which
                      caused a global crash because useNavigate threw with
                      no Router context. Do not move it back here. Ctrl/Cmd+K
                      now opens only the header search (MasterSearchPalette
                      in DashboardLayout). */}
                  <RouterProvider router={router} />
                </PipedreamProvider>
              </ToastProvider>
            </DateRangeProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export { DeskAssistantControlsPage } from './pages/DeskAssistantControlsPage';
export const DESK_ASSISTANT_CONTROLS_ROUTE = '/admin/assistant-controls' as const;
export { SabreSessionPoolPage } from './pages/SabreSessionPoolPage';
export const SABRE_SESSION_POOL_ROUTE = '/admin/sabre-pool' as const;

export default App;
